import { useForm, Link } from '@inertiajs/react'
import { useState, useMemo } from 'react'
import { Layout } from '@/components/Layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, ExternalLink, Clock, Activity, XCircle, CheckCircle, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'

interface Workflow {
  id: number
  external_workflow_id: string
  name: string
  is_active: boolean
  minutes_saved_per_success: number
  minutes_saved_per_failure: number
  client_name: string
  instance_name: string
  instance_id: number
  client_id: number
  executions_count: number
  success_rate: number
  failure_count: number
  time_saved_minutes: number
  n8n_url: string
}

interface Failure {
  id: number
  external_execution_id: string
  started_at: string | null
  finished_at: string | null
  duration_seconds: number | null
  error_category: string | null
  error_message: string | null
}

interface Props {
  workflow: Workflow
  recent_failures: Failure[]
  errors?: Record<string, string[]>
}

type FailureSortKey = 'external_execution_id' | 'started_at' | 'duration_seconds' | 'error_category' | 'error_message'
type SortDirection = 'asc' | 'desc'

function formatDate(dateString: string | null): string {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString()
}

function formatTimeSaved(minutes: number): string {
  if (minutes >= 60) {
    return `${(minutes / 60).toFixed(1)} hours`
  }
  return `${minutes} minutes`
}

function ErrorCategoryBadge({ category }: { category: string | null }) {
  if (!category) return <Badge variant="secondary">Unknown</Badge>

  const labels: Record<string, string> = {
    auth_permissions: 'Auth/Permissions',
    network_timeout: 'Network/Timeout',
    rate_limit: 'Rate Limit',
    upstream_error: 'Upstream Error',
    data_validation: 'Data/Validation',
    workflow_logic: 'Workflow Logic',
    unknown: 'Unknown',
  }

  return <Badge variant="outline">{labels[category] || category}</Badge>
}

export default function WorkflowShow({ workflow, recent_failures, errors = {} }: Props) {
  // Sorting state for failures table
  const [sortKey, setSortKey] = useState<FailureSortKey | null>('started_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const sortedFailures = useMemo(() => {
    if (!sortKey) return recent_failures
    return [...recent_failures].sort((a, b) => {
      let aVal: string | number | null
      let bVal: string | number | null

      if (sortKey === 'started_at') {
        aVal = a.started_at ? new Date(a.started_at).getTime() : null
        bVal = b.started_at ? new Date(b.started_at).getTime() : null
      } else if (sortKey === 'duration_seconds') {
        aVal = a.duration_seconds
        bVal = b.duration_seconds
      } else {
        aVal = a[sortKey]
        bVal = b[sortKey]
      }

      if (aVal == null && bVal == null) return 0
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }
      return sortDirection === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    })
  }, [recent_failures, sortKey, sortDirection])

  const handleSort = (key: FailureSortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ column }: { column: FailureSortKey }) => {
    if (sortKey !== column) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
    return sortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
  }

  const { data, setData, patch, processing } = useForm({
    workflow: {
      minutes_saved_per_success: workflow.minutes_saved_per_success,
      minutes_saved_per_failure: workflow.minutes_saved_per_failure,
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/workflows/${workflow.id}`)
  }

  const formData = data.workflow
  const setFormData = (key: keyof typeof formData, value: number) => {
    setData('workflow', { ...formData, [key]: value })
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link href="/workflows">
                <ArrowLeft className="h-4 w-4" />
                <span className="sr-only">Back</span>
              </Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{workflow.name}</h1>
              <p className="text-muted-foreground">
                <Link href={`/clients/${workflow.client_id}`} className="hover:underline">
                  {workflow.client_name}
                </Link>
                {' / '}
                <Link href={`/n8n_instances/${workflow.instance_id}`} className="hover:underline">
                  {workflow.instance_name}
                </Link>
              </p>
            </div>
          </div>
          <Button variant="outline" asChild>
            <a href={workflow.n8n_url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />
              Open in n8n
            </a>
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Status</CardTitle>
            </CardHeader>
            <CardContent>
              {workflow.is_active ? (
                <Badge variant="success">Active</Badge>
              ) : (
                <Badge variant="secondary">Inactive</Badge>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Executions (7 days)</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{workflow.executions_count}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${workflow.success_rate < 80 ? 'text-destructive' : ''}`}>
                {workflow.success_rate}%
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Time Saved</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatTimeSaved(workflow.time_saved_minutes)}</div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Time Saved Configuration</CardTitle>
            <CardDescription>
              Configure how much time each workflow execution saves.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="minutes_saved_per_success">Minutes saved per successful execution</Label>
                  <Input
                    id="minutes_saved_per_success"
                    type="number"
                    min="0"
                    value={formData.minutes_saved_per_success}
                    onChange={(e) => setFormData('minutes_saved_per_success', parseInt(e.target.value) || 0)}
                  />
                  {errors.minutes_saved_per_success && (
                    <p className="text-sm text-destructive">{errors.minutes_saved_per_success[0]}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="minutes_saved_per_failure">Minutes saved per failed execution</Label>
                  <Input
                    id="minutes_saved_per_failure"
                    type="number"
                    min="0"
                    value={formData.minutes_saved_per_failure}
                    onChange={(e) => setFormData('minutes_saved_per_failure', parseInt(e.target.value) || 0)}
                  />
                  <p className="text-sm text-muted-foreground">Usually 0, but some workflows still save time even when failing.</p>
                  {errors.minutes_saved_per_failure && (
                    <p className="text-sm text-destructive">{errors.minutes_saved_per_failure[0]}</p>
                  )}
                </div>
              </div>
              <Button type="submit" disabled={processing}>
                {processing ? 'Saving...' : 'Save Configuration'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" />
              Recent Failures
            </CardTitle>
            <CardDescription>
              The most recent failed executions for this workflow.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recent_failures.length === 0 ? (
              <div className="flex flex-col items-center py-8 text-muted-foreground">
                <CheckCircle className="h-8 w-8 mb-2" />
                <p>No recent failures!</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button onClick={() => handleSort('external_execution_id')} className="flex items-center font-medium hover:text-foreground">
                        Execution ID <SortIcon column="external_execution_id" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('started_at')} className="flex items-center font-medium hover:text-foreground">
                        Started At <SortIcon column="started_at" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('duration_seconds')} className="flex items-center font-medium hover:text-foreground">
                        Duration <SortIcon column="duration_seconds" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('error_category')} className="flex items-center font-medium hover:text-foreground">
                        Category <SortIcon column="error_category" />
                      </button>
                    </TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedFailures.map((failure) => (
                    <TableRow key={failure.id}>
                      <TableCell className="font-mono text-sm">
                        {failure.external_execution_id}
                      </TableCell>
                      <TableCell>{formatDate(failure.started_at)}</TableCell>
                      <TableCell>
                        {failure.duration_seconds ? `${failure.duration_seconds}s` : '-'}
                      </TableCell>
                      <TableCell>
                        <ErrorCategoryBadge category={failure.error_category} />
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                        {failure.error_message || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}
